import React, { useState, useEffect } from 'react';

const SOCIAL_FIELDS = [
  { key: 'facebook',  label: 'Facebook',  placeholder: 'https://facebook.com/yourpage' },
  { key: 'instagram', label: 'Instagram', placeholder: 'https://instagram.com/yourhandle' },
  { key: 'twitter',   label: 'X (Twitter)', placeholder: 'https://twitter.com/yourhandle' },
  { key: 'linkedin',  label: 'LinkedIn',  placeholder: 'https://linkedin.com/company/yourorg' },
  { key: 'whatsapp',  label: 'WhatsApp',  placeholder: 'https://wa.me/6512345678' }
];

function ContactInfoEditor({ contact, onUpdate, orgId, onSave, saving, savedMessage }) {
  const [form, setForm] = useState({
    address: '', phone: '', email: '', website: '',
    socialMedia: { facebook: '', instagram: '', twitter: '', linkedin: '', whatsapp: '' }
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (contact) {
      setForm({
        address: contact.address || '',
        phone: contact.phone || '',
        email: contact.email || '',
        website: contact.website || '',
        socialMedia: {
          facebook: contact.socialMedia?.facebook || '',
          instagram: contact.socialMedia?.instagram || '',
          twitter: contact.socialMedia?.twitter || '',
          linkedin: contact.socialMedia?.linkedin || '',
          whatsapp: contact.socialMedia?.whatsapp || ''
        }
      });
    }
  }, [contact]);

  const validate = (field, value) => {
    const errs = { ...errors };
    switch (field) {
      case 'email':
        if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) errs.email = 'Invalid email format';
        else delete errs.email;
        break;
      case 'phone':
        if (value && !/^[+\d\s\-()]{6,20}$/.test(value)) errs.phone = 'Invalid phone number';
        else delete errs.phone;
        break;
      case 'website':
        if (value && !value.startsWith('http://') && !value.startsWith('https://')) errs.website = 'URL must start with http:// or https://';
        else delete errs.website;
        break;
      default:
        if (field.startsWith('social_')) {
          const k = field.replace('social_', '');
          if (value && !value.startsWith('http://') && !value.startsWith('https://')) errs[field] = 'URL must start with http:// or https://';
          else delete errs[field];
        }
    }
    setErrors(errs);
    return !errs[field];
  };

  const handleField = (field, value) => {
    const updated = { ...form, [field]: value };
    setForm(updated);
    validate(field, value);
    onUpdate(updated);
  };

  const handleSocial = (key, value) => {
    const updatedSocial = { ...form.socialMedia, [key]: value };
    const updated = { ...form, socialMedia: updatedSocial };
    setForm(updated);
    validate(`social_${key}`, value);
    onUpdate(updated);
  };

  return (
    <div className="bb-editor">
      <div className="bb-editor-header">
        <div>
          <h2>Contact Information</h2>
          <p className="bb-editor-desc">How members and the public can reach your organisation.</p>
        </div>
        <div className="bb-editor-actions">
          {savedMessage && <span className={`bb-save-msg ${savedMessage.includes('Error') ? 'error' : ''}`}>{savedMessage}</span>}
          <button className="bb-btn-primary" onClick={onSave} disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</button>
        </div>
      </div>

      <div className="bb-form-section">
        <h3 className="bb-section-title">Primary Contact Details</h3>

        <div className="bb-form-group">
          <label htmlFor="address">Office / Mailing Address</label>
          <textarea id="address" rows={3} className="bb-textarea" value={form.address}
            onChange={(e) => handleField('address', e.target.value)}
            placeholder="e.g. Blk 123 Buona Vista Road, #02-45, Singapore 123456"
          />
        </div>

        <div className="bb-form-row">
          <div className="bb-form-group">
            <label htmlFor="phone">Phone Number</label>
            <input id="phone" type="tel" className={`bb-input ${errors.phone ? 'bb-input--error' : ''}`}
              value={form.phone} onChange={(e) => handleField('phone', e.target.value)}
              placeholder="+65 6123 4567"
            />
            {errors.phone && <span className="bb-field-error">{errors.phone}</span>}
          </div>

          <div className="bb-form-group">
            <label htmlFor="contactEmail">Email Address</label>
            <input id="contactEmail" type="email" className={`bb-input ${errors.email ? 'bb-input--error' : ''}`}
              value={form.email} onChange={(e) => handleField('email', e.target.value)}
              placeholder="contact@yourorganisation.org"
            />
            {errors.email && <span className="bb-field-error">{errors.email}</span>}
          </div>
        </div>

        <div className="bb-form-group">
          <label htmlFor="website">Website URL</label>
          <input id="website" type="url" className={`bb-input ${errors.website ? 'bb-input--error' : ''}`}
            value={form.website} onChange={(e) => handleField('website', e.target.value)}
            placeholder="https://www.yourorganisation.org.sg"
          />
          {errors.website && <span className="bb-field-error">{errors.website}</span>}
        </div>
      </div>

      <div className="bb-form-section">
        <h3 className="bb-section-title">Social Media Channels</h3>
        <p className="bb-editor-desc">Add your organisation's social media profiles to help members connect.</p>

        <div className="bb-social-grid">
          {SOCIAL_FIELDS.map(({ key, label, placeholder }) => (
            <div key={key} className="bb-form-group">
              <label htmlFor={`social_${key}`}>
                <span className="bb-social-icon">{getSocialIcon(key)}</span> {label}
              </label>
              <input
                id={`social_${key}`} type="url"
                className={`bb-input ${errors[`social_${key}`] ? 'bb-input--error' : ''}`}
                value={form.socialMedia[key]}
                onChange={(e) => handleSocial(key, e.target.value)}
                placeholder={placeholder}
              />
              {errors[`social_${key}`] && <span className="bb-field-error">{errors[`social_${key}`]}</span>}
            </div>
          ))}
        </div>
      </div>

      <div className="bb-form-section">
        <h3 className="bb-section-title">Preview</h3>
        <div className="bb-preview-card bb-contact-preview">
          {form.address && <div className="bb-contact-row"><span className="bb-contact-icon">📍</span><span>{form.address}</span></div>}
          {form.phone && <div className="bb-contact-row"><span className="bb-contact-icon">📞</span><span>{form.phone}</span></div>}
          {form.email && <div className="bb-contact-row"><span className="bb-contact-icon">✉️</span><span>{form.email}</span></div>}
          {form.website && <div className="bb-contact-row"><span className="bb-contact-icon">🌐</span><a href={form.website} target="_blank" rel="noreferrer">{form.website}</a></div>}
          {Object.entries(form.socialMedia).some(([, v]) => v) && (
            <div className="bb-contact-row">
              <span className="bb-contact-icon">🔗</span>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                {SOCIAL_FIELDS.filter(s => form.socialMedia[s.key]).map(s => (
                  <a key={s.key} href={form.socialMedia[s.key]} target="_blank" rel="noreferrer" className="bb-social-link">
                    {s.label}
                  </a>
                ))}
              </div>
            </div>
          )}
          {!form.address && !form.phone && !form.email && (
            <p className="bb-preview-empty">Contact details will appear here once you fill in the fields above.</p>
          )}
        </div>
      </div>
    </div>
  );
}

function getSocialIcon(key) {
  const icons = { facebook: '📘', instagram: '📷', twitter: '🐦', linkedin: '💼', whatsapp: '💬' };
  return icons[key] || '🔗';
}

export default ContactInfoEditor;
